import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { playGiftSound } from "../lib/giftSounds";
import CustomGiftPreview from "./CustomGiftPreview";

/**
 * Gift overlay played when a gift is sent.
 * - Video gifts (gift.video_url): full-screen MP4 animation with its own audio.
 * - Emoji gifts: cinematic 3D-style emoji burst + synthesised SFX.
 */
export default function GiftBurst({ gift, onDone }) {
  const [visible, setVisible] = useState(true);
  const videoRef = useRef(null);
  const isVideo = !!gift?.video_url;
  const isCustom = !!gift?.custom;

  useEffect(() => {
    if (!gift) return;
    if (!isVideo && (!isCustom || gift.sound_kind)) {
      try { playGiftSound(gift.sound_kind); } catch { /* ignore */ }
    }
    const life = isVideo ? 12000 : 3300; // video fallback cap; onEnded dismisses sooner
    const t = setTimeout(() => setVisible(false), life);
    const t2 = setTimeout(() => onDone?.(), life + 500);
    return () => { clearTimeout(t); clearTimeout(t2); };
  }, [gift, onDone, isVideo]);

  useEffect(() => {
    if (!isVideo || !videoRef.current) return;
    const v = videoRef.current;
    v.play().catch(() => {
      v.muted = true;
      v.play().catch(() => {});
    });
  }, [isVideo]);

  if (!gift) return null;
  const emoji = gift.emoji || "🎁";
  const isVip = (gift.tier || "").toLowerCase() === "vip";
  const particles = Array.from({ length: isVip ? 18 : 12 });

  const dismiss = () => {
    setVisible(false);
    setTimeout(() => onDone?.(), 400);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="gift-burst"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className={`fixed inset-0 z-[100] flex items-center justify-center ${
            isVideo ? "bg-ink/80 pointer-events-auto" : "pointer-events-none bg-gradient-to-b from-ink/30 via-transparent to-ink/30"
          }`}
          style={{ perspective: 1200 }}
          onClick={isVideo ? dismiss : undefined}
        >
          {isCustom ? (
            <>
              <motion.div
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 1.2, opacity: 0 }}
                transition={{ duration: 0.4 }}
                data-testid="gift-burst-custom"
                className="shadow-2xl rounded-3xl"
              >
                <CustomGiftPreview gift={{ ...gift.custom, emoji: gift.emoji, image_url: gift.image_url }} size={280} />
              </motion.div>
              <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.5, delay: 0.3 }} className="absolute bottom-24">
                <div className="crystal-glass px-5 py-3 rounded-full text-center">
                  <p className="text-papaya text-[10px] tracking-[0.35em] uppercase font-black">Community Gift</p>
                  <p className="text-cream text-2xl font-black tracking-tighter mt-0.5">{gift.label}</p>
                  <p className="text-cream/60 text-[11px] mt-0.5">{gift.waves} waves{gift.custom.creator_username ? ` · designed by @${gift.custom.creator_username}` : ""}</p>
                </div>
              </motion.div>
            </>
          ) : isVideo ? (
            <>
              <motion.video
                ref={videoRef}
                data-testid="gift-burst-video"
                src={gift.video_url}
                playsInline
                autoPlay
                onEnded={dismiss}
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 1.15, opacity: 0 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                className="max-w-full max-h-[75vh] object-contain rounded-2xl shadow-2xl"
              />
              <motion.div
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="absolute bottom-16"
              >
                <div className="crystal-glass px-5 py-3 rounded-full text-center">
                  <p className="text-papaya text-[10px] tracking-[0.35em] uppercase font-black">Gift Sent</p>
                  <p className="text-cream text-2xl font-black tracking-tighter mt-0.5">{gift.label}</p>
                  <p className="text-cream/60 text-[11px] mt-0.5">{gift.waves} waves · tap to dismiss</p>
                </div>
              </motion.div>
            </>
          ) : (
            <>
              {/* Particle ring */}
              {particles.map((_, i) => {
                const angle = (i / particles.length) * Math.PI * 2;
                const radius = 220;
                const dx = Math.cos(angle) * radius;
                const dy = Math.sin(angle) * radius;
                return (
                  <motion.span
                    key={i}
                    initial={{ x: 0, y: 0, scale: 0, opacity: 0 }}
                    animate={{ x: dx, y: dy, scale: 1.1, opacity: 1 }}
                    exit={{ opacity: 0, scale: 0 }}
                    transition={{ duration: 1.2, delay: 0.2 + (i * 0.02), ease: "easeOut" }}
                    className="absolute text-3xl"
                    style={{ filter: "drop-shadow(0 0 8px rgba(255,140,40,0.5))" }}
                  >
                    {emoji}
                  </motion.span>
                );
              })}

              {/* Center 3D-style emoji */}
              <motion.div
                initial={{ scale: 0.1, rotateY: -180, rotateX: 60, opacity: 0 }}
                animate={{
                  scale: [0.1, 1.5, 1.2, 1.35],
                  rotateY: [-180, 20, -10, 0],
                  rotateX: [60, -10, 5, 0],
                  opacity: 1,
                }}
                exit={{ scale: 2.0, opacity: 0, rotateY: 180 }}
                transition={{ duration: 2.0, ease: "easeOut", times: [0, 0.4, 0.7, 1] }}
                className="text-[180px] sm:text-[240px]"
                style={{
                  transformStyle: "preserve-3d",
                  filter: "drop-shadow(0 12px 32px rgba(255,140,40,0.6)) drop-shadow(0 0 60px rgba(0,150,80,0.4))",
                }}
                aria-hidden
              >
                {emoji}
              </motion.div>

              {/* Label */}
              <motion.div
                initial={{ y: 40, opacity: 0 }}
                animate={{ y: 180, opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="absolute"
              >
                <div className="crystal-glass px-5 py-3 rounded-full text-center">
                  <p className="text-papaya text-[10px] tracking-[0.35em] uppercase font-black">Gift Sent</p>
                  <p className="text-cream text-2xl font-black tracking-tighter mt-0.5">{gift.label}</p>
                  <p className="text-cream/60 text-[11px] mt-0.5">{gift.waves} waves</p>
                </div>
              </motion.div>
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
